// @flow strict
import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import {isEmpty} from 'lodash';
import {useHistory} from 'src/rerouter';
import type {JOB_INFORMATION} from 'src/types/crm.js';

import {useDispatch} from 'react-redux';
// $FlowFixMe[nonstrict-import]
import * as reduxApi from 'src/utils/redux-api-v2';

import {getJobApprovalStatuses} from 'src/action-creators/crm';

import {
  SubTitleLarge,
  BodyMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {TabList, Tab} from '@spaced-out/ui-design-system/lib/components/Tabs';
import User from 'src/components/crm/lib/user/user.jsx';
// $FlowFixMe[untyped-import]
import {formatMomentDateAndTime, formatDateTime} from 'src/utils/date-time';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';
import {Icon} from '@spaced-out/ui-design-system/lib/components/Icon';
import {
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@spaced-out/ui-design-system/lib/components/Modal';
import {
  Toast,
  TOAST_SEMANTIC,
  ToastTitle,
  toastApi,
} from '@spaced-out/ui-design-system/lib/components/Toast';
import {
  Panel,
  PanelBody,
  PanelHeader,
  PanelFooter,
} from '@spaced-out/ui-design-system/lib/components/Panel';
import {Textarea} from '@spaced-out/ui-design-system/lib/components/Textarea';

import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {JobDetailsTab} from 'src/components/crm/lib/job-details-tab/job-details-tab.jsx';
import {JobDescription} from 'src/components/crm/lib/job-description/job-description.jsx';
import {ApprovalStatus} from './tabs/approval-status.jsx';
import css from './approve-job-form.css';


export const ApproveJobForm = ({
  isOpen,
  handleClose,
  handleMoveBack,
  currentJobId,
  currentApprovalId,
  isRejected = false,
  isApproved = false,
  onSubmit,
}: {
  isOpen: boolean,
  handleClose: (queryObject?: {+[string]: string}) => void,
  handleMoveBack: (refetch: boolean, removeJobId?: boolean) => void,
  currentJobId?: string,
  currentApprovalId?: string,
  isRejected?: boolean,
  isApproved?: boolean,
  onSubmit?: () => void,
}): React.Node => {
  const history = useHistory();
  const {location} = history;
  const dispatch = useDispatch();
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;

  const approvalId: string = location.query.approvalId ?? currentApprovalId;
  const jobId = location.query.jobId || currentJobId || '';
  const [jobDetails, setJobDetails] = React.useState<JOB_INFORMATION | null>(
    null,
  );
  const [isLoadingJobData, setIsLoadingJobData] = React.useState(false);
  const [selectedOption, setSelectedTabOption] = React.useState({
    label: t('Job_Details', 'Job Details'),
    tabId: 'jobDetails',
  });
  const [modalState, setModalState] = React.useState<
    'approve' | 'reject' | null,
  >(null);
  const [jobApprovalMessage, setJobApprovalMessage] = React.useState('');
  const [approvingJob, setApprovingJob] = React.useState(false);
  const [jobApprovalObject, setJobApprovalObject] = React.useState({});
  const [approverStatusesList, setApproverStatusesList] = React.useState([]);

  const tabComponents = {
    jobDetails: JobDetailsTab,
    jobDescription: JobDescription,
    approvalStatus: ApprovalStatus,
  };

  // $FlowFixMe[incompatible-type]
  const SelectedTabComponent = tabComponents[selectedOption.tabId] || null;

  const closePanelHandler = () => {
    handleClose();
  };

  const handleApprovalAction = async (status, messages) => {
    setApprovingJob(true);
    const payload = {status, comment: jobApprovalMessage};
    try {
      await dispatch(
        reduxApi.post(
          `api/job-approval-statuses/${approvalId}`,
          payload,
          {},
          {apiPath: '/trmapi/'},
        ),
      );
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.success}>
          <ToastTitle>{messages.successMessage}</ToastTitle>
        </Toast>,
        {timeout: 2000},
      );
      if (onSubmit) {
        onSubmit();
      }
      handleMoveBack(true, true);
    } catch (error) {
      toastApi.show(
        <Toast semantic={TOAST_SEMANTIC.danger}>
          <ToastTitle>{messages.errorMessage}</ToastTitle>
        </Toast>,
        {timeout: 2000},
      );
    } finally {
      setApprovingJob(false);
    }
  };

  React.useEffect(() => {
    const fetchJobDetails = async () => {
      try {
        setIsLoadingJobData(true);

        // Run all API calls simultaneously
        const [response, approvals, approvalStatusList] = await Promise.all([
          dispatch(
            reduxApi.get(`api/jobs/${jobId}`, {}, {apiPath: `/trmapi/`}),
          ),
          dispatch(
            reduxApi.get(
              `api/job-approval-chains/${jobId}`,
              {},
              {apiPath: `/trmapi/`},
            ),
          ),
          dispatch(getJobApprovalStatuses(jobId)),
        ]);

        const jobApproval = approvals.find(
          (approval) => approval.type === 'JOB',
        );

        setJobDetails(response);
        setJobApprovalObject(jobApproval);
        setApproverStatusesList(approvalStatusList);
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoadingJobData(false);
      }
    };

    fetchJobDetails();
  }, [jobId]);

  const renderFooter = () => {
    if (isRejected || isApproved) {
      return null;
    }

    return (
      <PanelFooter>
        <div className={css.row}>
          <Button
            classNames={{wrapper: css.rejectButton}}
            onClick={() => setModalState('reject')}
          >
            {t('Reject', 'Reject')}
          </Button>
          <Button onClick={() => setModalState('approve')}>
            {t('Approve', 'Approve')}
          </Button>
        </div>
      </PanelFooter>
    );
  };
  const {user} = jobDetails ?? {};
  const renderStatusAlert = () => {
    if (isRejected) {
      return (
        // $FlowFixMe[incompatible-type]
        <InContextAlert leftIconName="shield-exclamation" semantic="danger">
          {t('Rejected_by', 'Rejected by')}:{' '}
          <User userId={parseInt(jobApprovalObject.updatedBy)} />
          {`${t('on', 'on')} `}
          {formatMomentDateAndTime(jobDetails?.updatedOn)}
        </InContextAlert>
      );
    }

    if (isApproved) {
      return (
        // $FlowFixMe[incompatible-type]
        <InContextAlert leftIconName="circle-check" semantic="success">
          {t('Approved_by', 'Approved by')}:{' '}
          <User userId={parseInt(jobApprovalObject.updatedBy)} />,
          {t('on', 'on')} {formatMomentDateAndTime(jobDetails?.updatedOn)}
        </InContextAlert>
      );
    }

    return (
      <div className={css.taskRow}>
        <Icon name="info-circle" color="secondary" />
        <BodyMedium color="secondary">
          {`${t(
            'Job_Approval_requested_on',
            'Job Approval requested on',
          )} ${formatDateTime(jobDetails?.updatedOn)} ${
            user ? `${t('by', 'by')} ${user.fullName}` : ''
          }`}
        </BodyMedium>
      </div>
    );
  };

  return (
    <Panel
      anchor="right"
      isOpen={isOpen}
      onClose={() => handleMoveBack(false)}
      size="large"
      tapOutsideToClose
      hideBackdrop={false}
    >
      <PanelHeader onCloseButtonClick={closePanelHandler} size="medium">
        <SubTitleLarge>
          {isRejected || isApproved
            ? t('Review_Job_Approval', 'Review Job Approval')
            : `${t('Approve', 'Approve')} ${
                jobDetails && jobDetails.title
                  ? jobDetails.title
                  : t('Job', 'Job')
              }`}
        </SubTitleLarge>
      </PanelHeader>
      <PanelBody className={css.panelBody}>
        <div className={css.reviewWrapper}>
          {isLoadingJobData ? (
            <div className={css.loader}>
              <CircularLoader />
            </div>
          ) : !isEmpty(jobDetails) ? (
            <>
              <div className={css.tabListWrapper}>
                <TabList
                  onSelect={setSelectedTabOption}
                  selectedTab={selectedOption}
                  size="medium"
                >
                  <Tab
                    label={t('Job_Details', 'Job Details')}
                    tabId="jobDetails"
                  />
                  <Tab
                    label={t('Job_Description', 'Job Description')}
                    tabId="jobDescription"
                  />
                  {!isEmpty(jobApprovalObject) ? (
                    <Tab
                      label={t('Approval_Status', 'Approval Status')}
                      tabId="approvalStatus"
                    />
                  ) : null}
                </TabList>
              </div>
              {(selectedOption.tabId === 'approvalStatus' ||
                selectedOption.tabId === 'jobDetails') &&
                renderStatusAlert()}
              {jobDetails &&
                (selectedOption.tabId === 'approvalStatus' ? (
                  <ApprovalStatus
                    approvalObject={jobApprovalObject}
                    approverStatusesList={approverStatusesList}
                  />
                ) : selectedOption.tabId === 'jobDetails' ? (
                  <JobDetailsTab jobDetails={jobDetails} />
                ) : selectedOption.tabId === 'jobDescription' ? (
                  <JobDescription jobInfo={jobDetails} />
                ) : null)}
              {modalState === 'approve' && (
                <ApproveModal
                  message={jobApprovalMessage}
                  setMessage={setJobApprovalMessage}
                  onConfirm={() =>
                    handleApprovalAction('APPROVED', {
                      successMessage: `${t(
                        'Job_Approved_successfully',
                        'Job Approved successfully',
                      )}!`,
                      errorMessage: `${t(
                        'Unable_to_approve_job',
                        'Unable to approve job',
                      )}`,
                    })
                  }
                  onClose={() => setModalState(null)}
                  isLoading={approvingJob}
                  t={t}
                />
              )}
              {modalState === 'reject' && (
                <RejectModal
                  message={jobApprovalMessage}
                  setMessage={setJobApprovalMessage}
                  onConfirm={() =>
                    handleApprovalAction('REJECTED', {
                      successMessage: `${t(
                        'Job_Rejected_successfully',
                        'Job Rejected successfully',
                      )}!`,
                      errorMessage: `${t(
                        'Unable_to_reject_job',
                        'Unable to reject job',
                      )}`,
                    })
                  }
                  onClose={() => setModalState(null)}
                  isLoading={approvingJob}
                  disabled={!jobApprovalMessage}
                  t={t}
                />
              )}
            </>
          ) : null}
        </div>
      </PanelBody>

      {renderFooter()}
    </Panel>
  );
};

const ApproveModal = ({
  message,
  setMessage,
  onConfirm,
  onClose,
  isLoading,
  t,
}) => (
  <ApprovalModal
    title={t('Confirm_Approval', 'Confirm Approval')}
    message={message}
    setMessage={setMessage}
    onConfirm={onConfirm}
    onClose={onClose}
    isLoading={isLoading}
    confirmText={t('Approve', 'Approve')}
    t={t}
  />
);

const RejectModal = ({
  message,
  setMessage,
  onConfirm,
  onClose,
  isLoading,
  disabled,
  t,
}) => (
  <ApprovalModal
    title={t('Reject_Approval', 'Reject Approval')}
    message={message}
    setMessage={setMessage}
    onConfirm={onConfirm}
    onClose={onClose}
    isLoading={isLoading}
    confirmText={t('Reject', 'Reject')}
    required
    disabled={disabled}
    t={t}
  />
);

const ApprovalModal = ({
  title,
  message,
  setMessage,
  onConfirm,
  onClose,
  isLoading,
  confirmText,
  required = false,
  disabled = false,
  t,
}) => (
  <Modal
    initialFocus={1}
    onClose={onClose}
    isOpen
    classNames={{content: css.approvalModalContainer}}
  >
    <ModalHeader onCloseButtonClick={onClose}>
      <SubTitleLarge>{title}</SubTitleLarge>
    </ModalHeader>
    <ModalBody>
      <Textarea
        label={
          required
            ? t('Add_Reason', 'Add Reason')
            : t('Add_Comment_Optional', 'Add Comment (Optional)')
        }
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        required={required}
      />
    </ModalBody>
    <ModalFooter>
      <Button onClick={onClose} type="ghost">
        {t('Close', 'Close')}
      </Button>
      <Button onClick={onConfirm} isLoading={isLoading} disabled={disabled}>
        {confirmText}
      </Button>
    </ModalFooter>
  </Modal>
);
